import { Injectable } from "@angular/core";
import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  AlcanceReporteQa,
  CalidadCodigoReq,
  CalidadCodigoRes,
  ContenidoReporteQa,
  FiltroCalidadCodigo,
  ProyectoConfiguracion,
} from "../shared/model/calidad-codigo/calidad-codigo.model";

/**
 * Calidad de código (SonarQube): puerta única del módulo hacia el backend
 * (docs/CALIDAD_CODIGO.md §4). Un método por endpoint, cada uno POST de
 * CalidadCodigoReq a CalidadCodigoRes, salvo el Excel de un folio (GET con
 * ?folio=, blob). El usuario nunca viaja: el servidor lo toma de la sesión.
 * Los errores de negocio llegan en un 200 con codigoError/mensajeError
 * (AUTH-001 sin sesión, COD_ERROR_00002 sin permiso); quien llama los revisa,
 * como en el BSC. El navegador nunca habla con SonarQube: lee la foto que
 * guarda el COS.
 */
@Injectable({ providedIn: "root" })
export class CalidadCodigoService {
  constructor(private readonly http: HttpClient) {}

  /** El tablero: proyectos ya filtrados y ordenados, conteos de los chips, gates y estado de la conexión. */
  tablero(filtro: FiltroCalidadCodigo, busqueda: string, gate: string | null): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { filtro };
    if (busqueda.trim() !== "") {
      req.busqueda = busqueda.trim();
    }
    if (gate) {
      req.gate = gate;
    }
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_TABLERO, req);
  }

  /** El detalle de un proyecto (por el id del COS): criterios, medidas, enlaces y las series del historial. */
  proyecto(idProyecto: number): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { idProyecto };
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_PROYECTO, req);
  }

  /** Los análisis guardados de un proyecto, paginados (1-based), más recientes primero. */
  historial(idProyecto: number, pagina: number, porPagina: number): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { idProyecto, pagina, porPagina };
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_HISTORIAL, req);
  }

  /** «Actualizar»: una pasada de sincronización para un proyecto (por clave) o para los 12. */
  actualizar(claveProyecto: string | null = null): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = {};
    if (claveProyecto) {
      req.claveProyecto = claveProyecto;
    }
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_ACTUALIZAR, req);
  }

  /** Congela la foto de un proyecto y le da folio y huella; contesta `reporte` con sus datos. */
  reporteProyecto(idProyecto: number): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { tipoReporte: "PROYECTO", idProyecto };
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_REPORTE_EMITIR, req);
  }

  /**
   * El consolidado para QA, con folio y huella. `alcance` (filtro, búsqueda y
   * gate del tablero) queda congelado en la foto; sin `idsProyectos` entran
   * los proyectos del tablero con ese alcance (seleccion FILTRO) y con ids
   * solo esos (seleccion ELEGIDOS).
   */
  reporteQa(alcance: AlcanceReporteQa, idsProyectos: number[] | null, contenido: ContenidoReporteQa): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { tipoReporte: "QA", filtro: alcance.filtro, contenido };
    const busqueda = (alcance.busqueda ?? "").trim();
    if (busqueda !== "") {
      req.busqueda = busqueda;
    }
    if (alcance.gate) {
      req.gate = alcance.gate;
    }
    if (idsProyectos && idsProyectos.length > 0) {
      req.idsProyectos = idsProyectos;
    }
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_REPORTE_EMITIR, req);
  }

  /** La bandeja de folios emitidos (sin datos), paginada, más reciente primero. */
  reportes(pagina: number, porPagina: number): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { pagina, porPagina };
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_REPORTES, req);
  }

  /** La foto congelada de un folio, para volver a armar el PDF idéntico. */
  reporteDatos(folio: string): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { folio };
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_REPORTE_DATOS, req);
  }

  /**
   * El Excel de un folio. GET con la respuesta completa (observe: "response")
   * para leer el nombre de Content-Disposition; un error llega como status con
   * su mensaje en texto plano (ver bsc/bsc-descarga.ts).
   */
  reporteExcel(folio: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set("folio", folio);
    return this.http.get(UrlConstantes.CALIDAD_CODIGO_REPORTE_EXCEL, { params, observe: "response", responseType: "blob" });
  }

  /** La configuración por proyecto, la bitácora de sincronizaciones y el estado de la conexión. */
  configuracion(): Observable<CalidadCodigoRes> {
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_CONFIGURACION, {});
  }

  /** Guarda la configuración de UN proyecto; contesta la configuración completa ya guardada. */
  configuracionGuardar(configuracion: ProyectoConfiguracion): Observable<CalidadCodigoRes> {
    const req: CalidadCodigoReq = { configuracion };
    return this.http.post<CalidadCodigoRes>(UrlConstantes.CALIDAD_CODIGO_CONFIGURACION_GUARDAR, req);
  }
}
